import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { IconButton, CircularProgress } from '@mui/material';

import FormInputText from '../../../components/common/stories/FormInputText';
import FormButton from '../../../components/common/stories/FormButton';
import { useNotification } from '../../../context/NotificationContext';
import { cmsPagesApi } from '../services/storeWebsiteApi';
import StoreHoursEditor from './StoreHoursEditor';
import '../../settings/Settings.css';
import '../StoreWebsiteSettings.css';

const CMSPageEditor = () => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const locationId = searchParams.get('location');
  const notification = useNotification();

  const [page, setPage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Form fields
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [storeHours, setStoreHours] = useState(null);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setIsLoading(true);
        const data = await cmsPagesApi.getById(pageId);
        setPage(data);
        setTitle(data.title || '');
        setContent(data.content || '');
        setStoreHours(data.store_hours || null);
      } catch (error) {
        console.error('Error fetching page:', error);
        notification.error('Failed to load page');
        navigate(`/website/pages?location=${locationId}`);
      } finally {
        setIsLoading(false);
      }
    };

    if (pageId) {
      fetchPage();
    }
  }, [pageId, locationId, navigate, notification]);

  const handleSave = async () => {
    if (!title.trim()) {
      notification.error('Page title is required');
      return;
    }

    try {
      setIsSaving(true);
      const updateData = {
        title,
        content,
      };

      if (page.page_type === 'hours' && storeHours) {
        updateData.store_hours = storeHours;
      }

      await cmsPagesApi.update(pageId, updateData);
      notification.success('Page saved successfully');
    } catch (error) {
      console.error('Error saving page:', error);
      notification.error('Failed to save page');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigate(`/website/pages?location=${locationId}`);
  };

  if (isLoading) {
    return (
      <div className="dashboard-body-wrapper">
        <div className="main-column">
          <div className="div-block" style={{ textAlign: 'center', padding: 40 }}>
            <CircularProgress />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-body-wrapper">
      <div className="main-column">
        <div className="div-block">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <IconButton onClick={handleBack} size="small">
              <BackIcon />
            </IconButton>
            <span className="div-block-title">Edit Page: {page?.title}</span>
          </div>
        </div>

        <div className="div-block">
          <div className="settings-form">
            <div className="form-section">
              <h4 className="form-section-title">Page Settings</h4>

              <div className="form-row">
                <FormInputText
                  label=""
                  placeholder="Page Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  width="90%"
                  isRequired={true}
                />
              </div>
            </div>

            {page?.page_type === 'hours' ? (
              <div className="form-section">
                <h4 className="form-section-title">Store Hours</h4>
                <StoreHoursEditor
                  storeHours={storeHours}
                  onChange={setStoreHours}
                />
              </div>
            ) : (
              <div className="form-section">
                <h4 className="form-section-title">Page Content</h4>
                <p className="helper-text">
                  {page?.page_type === 'about'
                    ? 'Tell customers about your store, its history, and what makes it special.'
                    : page?.page_type === 'contact'
                    ? 'Provide contact information, address, and any additional details customers might need.'
                    : 'Enter the content for this page.'}
                </p>

                <textarea
                  placeholder="Enter your content here..."
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  style={{
                    width: '90%',
                    minHeight: 300,
                    padding: '15px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    fontSize: 14,
                    fontFamily: 'inherit',
                    lineHeight: 1.6,
                    resize: 'vertical',
                  }}
                />
                <p className="helper-text" style={{ marginTop: 5 }}>
                  You can use basic formatting. Line breaks will be preserved.
                </p>
              </div>
            )}

            <div className="form-row" style={{ marginTop: 20 }}>
              <FormButton onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Page'}
              </FormButton>
              <FormButton
                onClick={handleBack}
                style={{ marginLeft: 10, background: '#666' }}
              >
                Cancel
              </FormButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CMSPageEditor;
